import { gql } from '@apollo/client';
import { MISSION_FRAGMENT } from '../liveops/components/apolloQueries';
import { HURDLE_QUIZ_FRAGMENT } from '../liveops/screens/HurdleQuiz/graphql';

export const GET_SESSIONS = gql`
  query getLogs($userID: ID, $installID: ID, $startDate: Date!, $endDate: Date) {
    analytics {
      id
      appSessions(userId: $userID, installId: $installID, startDate: $startDate, endDate: $endDate) {
        data
      }
    }
  }
`;

// Dry-run estimate ($0) of what the next "Load More" window would scan, to warn the admin before clicking.
export const GET_SESSIONS_COST_ESTIMATE = gql`
  query getSessionsCostEstimate($userID: ID, $installID: ID, $startDate: Date!, $endDate: Date) {
    analytics {
      id
      appSessionsCostEstimate(userId: $userID, installId: $installID, startDate: $startDate, endDate: $endDate) {
        bytes
        estimatedUsd
      }
    }
  }
`;

export const GET_SESSION_LOGS = gql`
  query getSessionLogs($sessionID: ID!, $sessionDate: Date!) {
    analytics {
      id
      appLogsBySession(sessionId: $sessionID, sessionDate: $sessionDate) {
        frontendLogs {
          data
          error
        }
        backendLogs {
          data
          error
        }
        crashLogs {
          data
          error
        }
      }
    }
  }
`;

// Dry-run estimate ($0) of what opening a session scans (front + back + crash). Backend logs dominate.
export const GET_SESSION_LOGS_COST_ESTIMATE = gql`
  query getSessionLogsCostEstimate($sessionID: ID!, $sessionDate: Date!) {
    analytics {
      id
      appLogsBySessionCostEstimate(sessionId: $sessionID, sessionDate: $sessionDate) {
        bytes
        estimatedUsd
      }
    }
  }
`;

export const GET_RUNTIME_LOGS = gql`
  query getRuntimeLogs($runtimeID: ID!, $offset: Int!, $limit: Int!, $startDate: Date) {
    analytics {
      id
      backendLogsByRuntime(runtimeId: $runtimeID, offset: $offset, limit: $limit, startDate: $startDate) {
        data
      }
    }
  }
`;

// Dry-run estimate ($0) of the single scan that opening a runtime costs. Load More there is in-memory
// and free, so this is the whole BigQuery cost of the runtime view.
export const GET_RUNTIME_LOGS_COST_ESTIMATE = gql`
  query getRuntimeLogsCostEstimate($runtimeID: ID!, $startDate: Date) {
    analytics {
      id
      backendLogsByRuntimeCostEstimate(runtimeId: $runtimeID, startDate: $startDate) {
        bytes
        estimatedUsd
      }
    }
  }
`;

export const GET_USER_FROM_SESSION = gql`
  query getUser($property: String!, $type: UserSearchType!) {
    user(property: $property, type: $type) {
      id
      displayName
      picture
      subscriptionTier
      phoneCredential
      emailCredential
      facebookCredential
      appleCredential
      googleCredential
      userLevel
      userLevelProgressToNextLevel
      coinBalance
      diamondBalance
      energySystems {
        tournamentTicket {
          extraBalance
        }
      }
      basicTierContentPack {
        contentPack {
          name
        }
      }
      ABTestList {
        id
        variation
      }
      stampCards
      totalStampCardsEarned
    }
  }
`;

export const GET_USERS_FROM_SESSION = gql`
  query getUsersFromSession($properties: [String!]!, $type: UserSearchType!) {
    users(properties: $properties, type: $type) {
      id
      displayName
      picture
      subscriptionTier
      phoneCredential
      emailCredential
      facebookCredential
      appleCredential
      googleCredential
      userLevel
      userLevelProgressToNextLevel
      coinBalance
      diamondBalance
      basicTierContentPack {
        contentPack {
          name
        }
      }
      ABTestList {
        id
        variation
      }
    }
  }
`;

export const BAN_USER_MUTATION = gql`
  mutation banUser($input: BanUserInput!) {
    banUser(input: $input) {
      user {
        id
        displayName
        picture
        role
      }
    }
  }
`;

export const UNBAN_USER_MUTATION = gql`
  mutation unbanUser($input: UnbanUserInput!) {
    unbanUser(input: $input) {
      user {
        id
        role
      }
    }
  }
`;

export const FORCE_USER_ABTEST_MUTATION = gql`
  mutation forceUserTest($input: ForceUserABTestInput!) {
    forceUserABTest(input: $input) {
      user {
        id
        ABTestList {
          id
          variation
        }
        ABTestForcedList {
          id
        }
      }
    }
  }
`;

const LOYALTY_FRAGMENT = gql`
  fragment LoyaltyFragment on Loyalty {
    futureCard {
      ...LoyaltyCardFragment
    }
    currentCard {
      ...LoyaltyCardFragment
    }
    previousCard {
      ...LoyaltyCardFragment
    }
    tradeableCardCount
    cardStreak
    dayStreak
    previousCardStreak
  }

  fragment LoyaltyCardFragment on LoyaltyCard {
    id
    isExpired
    isCompleted
    currentDayIndex
    nextClaimableDayIndex
    days {
      rewards {
        __typename
        ... on Currency {
          amount
        }
        ... on CurrencyBoost {
          boostType
        }
        ... on CurrencyUnlimitedBoost {
          unlimitedBoostType
        }
      }
      isClaimed
      claimedMultiplier
      claimedDate
      start
      end
    }
  }
`;

const MISSION_PROGRESS_FRAGMENT = gql`
  fragment MissionProgressFragment on MissionProgress {
    id
    mission {
      ...MissionFragment
    }
    progress
    progressGoal
    completedDate
    claimedDate
    claimedMultiplier
  }
  ${MISSION_FRAGMENT}
`;

const MONTHLY_PASS_MISSIONS_FRAGMENT = gql`
  fragment MonthlyPassMissionsFragment on UserMonthlyPassMissions {
    id
    dailyMissions {
      ...MissionProgressFragment
    }
    monthlyMissions {
      ...MissionProgressFragment
    }
    extraMission {
      ...MissionProgressFragment
    }
    extraMissionNo
  }
  ${MISSION_PROGRESS_FRAGMENT}
`;

const USER_FRAGMENT = gql`
  fragment UserViewerFragment on User {
    id
    displayName
    picture
    lastOnline
    emailCredential
    subscriptionTier
    subscriptionDetails {
      sku
      autoRenews
      activeSince
      expires
      isFreeTrial
    }
    userLevel
    userLevelProgressToNextLevel
    currentUserMonthlyPass {
      progressToNextLevel
      level
      missions {
        ...MonthlyPassMissionsFragment
      }
    }
    achievementMissions {
      id
      missions {
        ...MissionProgressFragment
      }
    }
    coinBalance
    diamondBalance
    claimedUpdateGiftId
    vipDays
    premiumDays
    energySystems {
      tournamentTicket {
        currentCapSupply
        extraBalance
      }
      coin {
        currentCapSupply
      }
      xp {
        currentCapSupply
      }
      liveEventTicket {
        currentCapSupply
      }
    }
    liveEventCurrency
    boostRemove2Balance
    boostTeamSpiritBalance
    boostReviveBalance
    boostNinjaBalance
    boostBombBalance
    boostSpeedsterBalance
    boostArtistOnlyBalance
    boostHurdleHintBalance
    stampCards
    totalStampCardsEarned
    basicTierContentPack {
      id
      contentPack {
        id
        name
        picture
      }
      level
      progressToNextLevel
      totalXP
      currentMonthXP
      accuracy
      highestScore
      averageScore
      roundPlayedCount
      contentDiscoveredCount
      totalContentItemsCount
    }
    fragmentBalance {
      id
      category {
        id
        name
      }
      amount
    }
    vanityPieceBalance {
      id
      amount
    }
    stickers {
      id
    }
    profileFrames {
      id
    }
    appSkins {
      id
    }
    ABTestList {
      id
      variation
    }
    ABTestForcedList {
      id
    }
    ABTestConfigs {
      id
      variations
      status
    }
    ABTestLastAppInstallList {
      id
      variation
    }
    role
    reports {
      reporter {
        id
      }
      created
      comment
    }
    unlimitedBoosts {
      unlimitedBoostRemove2Balance
      unlimitedBoostRemove2BalanceExpiration
      unlimitedBoostRemove2End
      unlimitedBoostSpeedsterBalance
      unlimitedBoostSpeedsterBalanceExpiration
      unlimitedBoostSpeedsterEnd
      unlimitedBoostCoinsAndXpBalance
      unlimitedBoostCoinsAndXpBalanceExpiration
      unlimitedBoostCoinsAndXpEnd
      unlimitedBoostAdFreeBalance
      unlimitedBoostAdFreeBalanceExpiration
      unlimitedBoostAdFreeEnd
      unlimitedBoostTournamentTicketBalance
      unlimitedBoostTournamentTicketBalanceExpiration
      unlimitedBoostTournamentTicketEnd
    }
    loyalty {
      ...LoyaltyFragment
    }
    earnedContentPackIdsRaw
    ownedContentPacks(limit: 1000) {
      list {
        contentPack {
          id
          popularity
          explicitContentStatus
          name
        }
      }
    }
    savedContentPacks(limit: 100) {
      list {
        id
        level
        progressToNextLevel
        totalXP
        currentMonthXP
        accuracy
        highestScore
        averageScore
        roundPlayedCount
        contentDiscoveredCount
        totalContentItemsCount
        contentPack {
          id
          name
          picture
        }
      }
    }
    recentContentPacks(limit: 5) {
      list {
        id
        level
        progressToNextLevel
        totalXP
        currentMonthXP
        accuracy
        highestScore
        averageScore
        roundPlayedCount
        contentDiscoveredCount
        totalContentItemsCount
        contentPack {
          id
          name
          picture
        }
      }
    }
    currentSpecialEvent {
      id
      meScoreDetails {
        type
        score
      }
    }
    piggyBank {
      coins
      diamonds
    }
    metaFragments {
      BRONZE {
        amount
      }
      SILVER {
        amount
      }
      GOLD {
        amount
      }
    }
    shards {
      BRONZE
      SILVER
      GOLD
    }
    accoladeBalance
    team {
      id
    }
    freeSpins
    calendars {
      id
      name
    }
  }

  ${LOYALTY_FRAGMENT}
  ${MONTHLY_PASS_MISSIONS_FRAGMENT}
  ${MISSION_FRAGMENT}
`;

export const RESET_USER_SUBSCRIPTIONS_MUTATION = gql`
  mutation resetUserSubscriptions($input: ResetUserSubscriptionsInput!) {
    resetUserSubscriptions(input: $input) {
      user {
        ...UserViewerFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const SET_SUBSCRIPTION_EXPIRES_MUTATION = gql`
  mutation setSubscriptionExpires($input: SetSubscriptionExpiresInput!) {
    setSubscriptionExpires(input: $input) {
      user {
        ...UserViewerFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const SEARCH_USER = gql`
  query searchUser($input: String!, $type: UserSearchType!) {
    user(property: $input, type: $type) {
      ...UserViewerFragment
      isInfluencer
      influencerId
      testCredential
    }
  }
  ${USER_FRAGMENT}
`;

export const ADMIN_USER_FRIEND_RELATIONS = gql`
  query adminUserFriendRelations($input: AdminUserFriendRelationsInput!) {
    adminUserFriendRelations(input: $input) {
      id
      hasMore
      totalCount
      list {
        id
        fromId
        toId
        rawState
        stateBeforeBlock
        displayStatus
        opponent {
          id
          displayName
          picture
        }
      }
    }
  }
`;

export const ADMIN_UNBLOCK_USER_FRIEND_RELATION = gql`
  mutation adminUnblockUserFriendRelation($input: AdminUnblockUserFriendRelationInput!) {
    adminUnblockUserFriendRelation(input: $input) {
      success
    }
  }
`;

export const ADMIN_BLOCK_USER_FRIEND_RELATION = gql`
  mutation adminBlockUserFriendRelation($input: AdminBlockUserFriendRelationInput!) {
    adminBlockUserFriendRelation(input: $input) {
      success
    }
  }
`;

export const ADMIN_FORCE_ADD_FRIEND_RELATION = gql`
  mutation adminForceAddFriendRelation($input: AdminForceAddFriendRelationInput!) {
    adminForceAddFriendRelation(input: $input) {
      success
    }
  }
`;

export const UPDATE_USER = gql`
  mutation updateUser($input: ForceUpdateUserInput!) {
    forceUpdateUser(input: $input) {
      user {
        ...UserViewerFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

const USER_PLAYLIST_PROGRESS_ADMIN_PAYLOAD = gql`
  fragment UserPlaylistProgressAdminPayload on UserContentPack {
    id
    level
    progressToNextLevel
    totalXP
    currentMonthXP
    accuracy
    highestScore
    averageScore
    roundPlayedCount
    contentDiscoveredCount
    totalContentItemsCount
    contentPack {
      id
      name
      picture
    }
  }
`;

export const USER_PLAYLIST_PROGRESS_ADMIN = gql`
  query userPlaylistProgressAdmin($input: UserPlaylistProgressLookupAdminInput!) {
    userPlaylistProgressAdmin(input: $input) {
      ...UserPlaylistProgressAdminPayload
    }
  }
  ${USER_PLAYLIST_PROGRESS_ADMIN_PAYLOAD}
`;

export const SAVE_USER_PLAYLIST_PROGRESS_ADMIN = gql`
  mutation saveUserPlaylistProgressAdmin($input: UserPlaylistProgressAdminInput!) {
    saveUserPlaylistProgressAdmin(input: $input) {
      userContentPack {
        ...UserPlaylistProgressAdminPayload
      }
    }
  }
  ${USER_PLAYLIST_PROGRESS_ADMIN_PAYLOAD}
`;

export const SET_USER_CURRENT_BASIC_CONTENT_PACK_ADMIN = gql`
  mutation setUserCurrentBasicContentPackAdmin($input: SetUserCurrentBasicContentPackAdminInput!) {
    setUserCurrentBasicContentPackAdmin(input: $input) {
      userContentPack {
        ...UserPlaylistProgressAdminPayload
      }
    }
  }
  ${USER_PLAYLIST_PROGRESS_ADMIN_PAYLOAD}
`;

export const UPDATE_USER_PLAYLIST_PROGRESS_ADMIN = gql`
  mutation updateUserPlaylistProgressAdmin($input: UserPlaylistProgressAdminInput!) {
    updateUserPlaylistProgressAdmin(input: $input) {
      userContentPack {
        ...UserPlaylistProgressAdminPayload
      }
    }
  }
  ${USER_PLAYLIST_PROGRESS_ADMIN_PAYLOAD}
`;

export const UNSAVE_USER_PLAYLIST_ADMIN = gql`
  mutation unsaveUserPlaylistAdmin($input: UnsaveUserPlaylistAdminInput!) {
    unsaveUserPlaylistAdmin(input: $input) {
      userContentPack {
        ...UserPlaylistProgressAdminPayload
      }
    }
  }
  ${USER_PLAYLIST_PROGRESS_ADMIN_PAYLOAD}
`;

export const FLUSH_USER_CACHE_MUTATION = gql`
  mutation refreshUserRedisCache($input: RefreshUserRedisCacheInput!) {
    refreshUserRedisCache(input: $input) {
      user {
        ...UserViewerFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const FLUSH_REDIS_CACHE_FOR_ONE_USER_MUTATION = gql`
  mutation flushRedisCacheForOneUser($input: FlushRedisCacheForOneUserInput!) {
    flushRedisCacheForOneUser(input: $input) {
      user {
        ...UserViewerFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const GET_USER_LAST_SESSION = gql`
  query getUserLastSession($id: ID!) {
    analytics {
      id
      userLastSession(id: $id) {
        id
        date
      }
    }
  }
`;

export const USER_GIFT_STATUS_ADMIN = gql`
  query userGiftStatusAdmin($userId: ID!) {
    userGiftStatusAdmin(userId: $userId) {
      isFriend
      isSystem
      sender {
        __typename
        ... on User {
          id
          displayName
        }
        ... on Agent {
          id
          displayName
        }
      }
      gift {
        id
        created
        claimed
        bundleName
        message
        rewards {
          __typename
          ... on Currency {
            amount
          }
          ... on CurrencyBoost {
            boostType
          }
          ... on CurrencyUnlimitedBoost {
            unlimitedBoostType
          }
          ... on CurrencyCategoryFragment {
            category {
              id
              name
            }
          }
          ... on CurrencyCategoryRandom {
            category {
              id
              name
            }
          }
          ... on AppSkin {
            id
            name
          }
          ... on Badge {
            id
            name
            badgeType: type
          }
          ... on ContentPackReward {
            id
            rewardType: type
            contentPack {
              id
              name
            }
          }
          ... on Emoji {
            id
            name
          }
          ... on EmojiPack {
            id
            name
          }
          ... on Poster {
            id
            name
            posterType: type
          }
          ... on ProfileFrame {
            id
            name
          }
          ... on Sticker {
            id
            name
          }
        }
      }
    }
  }
`;

export const USER_BADGES_ADMIN = gql`
  query userBadgesAdmin($userId: ID!) {
    userBadgesAdmin(userId: $userId) {
      id
      name
      type
      owned
      isSP2
      currentLevel
      details
      createdAt
      updatedAt
    }
  }
`;

export const GRANT_USER_BADGE_ADMIN = gql`
  mutation grantUserBadgeAdmin($input: GrantUserBadgeAdminInput!) {
    grantUserBadgeAdmin(input: $input) {
      badge {
        id
        name
        type
        currentLevel
      }
    }
  }
`;

export const REVOKE_USER_BADGE_ADMIN = gql`
  mutation revokeUserBadgeAdmin($input: RevokeUserBadgeAdminInput!) {
    revokeUserBadgeAdmin(input: $input) {
      userId
      badgeId
    }
  }
`;

export const SET_USER_BADGE_LEVEL_ADMIN = gql`
  mutation setUserBadgeLevelAdmin($input: SetUserBadgeLevelAdminInput!) {
    setUserBadgeLevelAdmin(input: $input) {
      badge {
        id
        name
        type
        currentLevel
      }
    }
  }
`;

export const APP_INFORMATION = gql`
  query appInformation {
    me {
      energySystems {
        coin {
          caps {
            basic {
              maxStorage
            }
            ad_free {
              maxStorage
            }
            premium {
              maxStorage
            }
            vip {
              maxStorage
            }
            plus {
              maxStorage
            }
          }
        }
        xp {
          caps {
            basic {
              maxStorage
            }
            ad_free {
              maxStorage
            }
            premium {
              maxStorage
            }
            vip {
              maxStorage
            }
            plus {
              maxStorage
            }
          }
        }
        tournamentTicket {
          caps {
            basic {
              maxStorage
            }
            ad_free {
              maxStorage
            }
            premium {
              maxStorage
            }
            vip {
              maxStorage
            }
            plus {
              maxStorage
            }
          }
        }
        liveEventTicket {
          caps {
            basic {
              maxStorage
            }
            ad_free {
              maxStorage
            }
            premium {
              maxStorage
            }
            vip {
              maxStorage
            }
            plus {
              maxStorage
            }
          }
        }
      }
    }
    app {
      contentCategories {
        id
        name
        icon
      }
    }
  }
`;

export const LIVE_OPS_CONTENT = gql`
  query liveOpsContent {
    liveOps {
      stickers {
        list {
          id
          name
          asset
          liveOpsAvailability
          nextLevelItems {
            level
          }
        }
      }
      appskins {
        list {
          id
          name
          iconAsset
          liveOpsAvailability
          nextLevelItems {
            level
          }
        }
      }
      profileFrames {
        list {
          id
          name
          asset
          liveOpsAvailability
          nextLevelItems {
            level
            asset
          }
        }
      }
    }
  }
`;

export const UPDATE_LOYALTY_CARD = gql`
  mutation updateLoyaltyCard($input: UpdateLoyaltyCardInput!) {
    updateLoyaltyCard(input: $input) {
      user {
        id
        loyalty {
          ...LoyaltyFragment
        }
      }
    }
  }
  ${LOYALTY_FRAGMENT}
`;

export const RESET_LOYALTY_CARD = gql`
  mutation resetLoyaltyCard($input: ResetLoyaltyCardInput!) {
    resetLoyaltyCard(input: $input) {
      user {
        id
        loyalty {
          ...LoyaltyFragment
        }
      }
    }
  }
  ${LOYALTY_FRAGMENT}
`;

export const REVIVE_LOYALTY_CARD = gql`
  mutation reviveLoyaltyCard($input: ReviveLoyaltyCardInput!) {
    reviveLoyaltyCard(input: $input) {
      user {
        id
        loyalty {
          ...LoyaltyFragment
        }
      }
    }
  }
  ${LOYALTY_FRAGMENT}
`;

export const SHIFT_LOYALTY_CARD = gql`
  mutation shiftLoyaltyCard($input: ShiftLoyaltyCardInput!) {
    shiftLoyaltyCard(input: $input) {
      user {
        id
        loyalty {
          ...LoyaltyFragment
        }
      }
    }
  }
  ${LOYALTY_FRAGMENT}
`;

export const UPDATE_USER_SPECIAL_EVENT_POINTS = gql`
  mutation updateUserSpecialEventPoints($input: UpdateUserSpecialEventPointsInput!) {
    updateUserSpecialEventPoints(input: $input) {
      user {
        id
        currentSpecialEvent {
          meScoreDetails {
            type
            score
          }
        }
      }
    }
  }
`;

export const GET_USER_SPECIAL_EVENT_STATE = gql`
  query getUserSpecialEventState($property: String!, $type: UserSearchType!) {
    user(property: $property, type: $type) {
      id
      currentSpecialEvent {
        id
        name
        meScore
        isRankGroupRewardClaimed
        milestones {
          id
          score
          isClaimed
        }
        plusMilestones {
          id
          score
          isClaimed
        }
        userMissions {
          id
          activeSequentialMissionIdx
          sequentialMissions {
            id
            progress
            progressGoal
            completedDate
            claimedDate
          }
        }
      }
    }
  }
`;

export const RESET_USER_SPECIAL_EVENT = gql`
  mutation resetUserSpecialEvent($input: ResetUserSpecialEventInput!) {
    resetUserSpecialEvent(input: $input) {
      user {
        id
      }
      specialEvent {
        id
        meScore
        meScoreDetails {
          type
          score
        }
        isRankGroupRewardClaimed
        milestones {
          id
          isClaimed
        }
        plusMilestones {
          id
          isClaimed
        }
      }
    }
  }
`;

export const GET_ADMIN_TOURNAMENT_LIST = gql`
  query getAdminTournamentList($limit: Int, $offset: Int) {
    liveOps {
      id
      tournaments(limit: $limit, offset: $offset) {
        id
        totalCount
        list {
          id
          name
          startDate
          endDate
        }
      }
    }
  }
`;

export const GET_USER_TOURNAMENT_STATE = gql`
  query getUserTournamentState($userId: String!, $tournamentId: ID!) {
    user(property: $userId, type: BY_ID) {
      id
      adminTournament(tournamentId: $tournamentId) {
        id
        name
        startDate
        endDate
        meScore
        isRankGroupRewardClaimed
        currentRound {
          id
          state
        }
        userRankGroup {
          id
          minRank
          maxRank
        }
        milestones {
          id
          score
          isClaimed
        }
      }
    }
  }
`;

export const UPDATE_USER_TOURNAMENT_SCORE = gql`
  mutation updateUserTournamentScore($input: UpdateUserTournamentScoreInput!) {
    updateUserTournamentScore(input: $input) {
      user {
        id
      }
      tournament {
        id
        meScore
        milestones {
          id
          isClaimed
        }
      }
    }
  }
`;

export const RESET_USER_TOURNAMENT = gql`
  mutation resetUserTournament($input: ResetUserTournamentInput!) {
    resetUserTournament(input: $input) {
      user {
        id
      }
      tournament {
        id
        meScore
        isRankGroupRewardClaimed
        currentRound {
          id
          state
        }
        milestones {
          id
          isClaimed
        }
      }
    }
  }
`;

export const UPDATE_USER_INFLUENCER = gql`
  mutation updateUserInfluencer($input: UpdateUserInfluencerInput!) {
    updateUserInfluencer(input: $input) {
      user {
        ...UserViewerFragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const GET_USER_LOYALTY_CAMPAIGN_PROGRESS = gql`
  query getUserLoyaltyCampaignProgress($userId: String!) {
    user(property: $userId, type: BY_ID) {
      id
      loyaltyCampaignProgress {
        loyaltyPoints
        claimedLoyaltyCards
      }
      loyaltyCampaignRewardLogs {
        referenceId
        sourceType
        productId
        amount
        created
      }
    }
    liveOps {
      id
      loyaltyCampaignCards {
        id
        name
      }
    }
  }
`;

export const SET_USER_LOYALTY_CAMPAIGN_PROGRESS = gql`
  mutation setUserLoyaltyCampaignProgress($userId: ID!, $loyaltyPoints: Int!, $claimedLoyaltyCardIds: [ID!]!) {
    setUserLoyaltyCampaignProgress(
      userId: $userId
      loyaltyPoints: $loyaltyPoints
      claimedLoyaltyCardIds: $claimedLoyaltyCardIds
    ) {
      id
      loyaltyCampaignProgress {
        loyaltyPoints
        claimedLoyaltyCards
      }
    }
  }
`;

export const RESET_USER_LOYALTY_CAMPAIGN_PROGRESS = gql`
  mutation resetUserLoyaltyCampaignProgress($userId: ID!) {
    resetUserLoyaltyCampaignProgress(userId: $userId) {
      id
      loyaltyCampaignProgress {
        loyaltyPoints
        claimedLoyaltyCards
      }
    }
  }
`;

export const SEND_RETENTION_REMINDER = gql`
  mutation sendRetentionReminder($input: SendRetentionReminderInput) {
    sendRetentionReminder(input: $input)
  }
`;

export const SEND_NOTIFICATION = gql`
  mutation sendNotification($input: SendNotificationInput!) {
    sendNotification(input: $input)
  }
`;

export const USER_TRANSACTIONS = gql`
  query userTransactions($userId: ID!, $startDate: String!, $endDate: String!) {
    analytics {
      userTransactions(userId: $userId, startDate: $startDate, endDate: $endDate)
    }
  }
`;

export const ADMIN_USER_SPEND_TIER = gql`
  query adminUserSpendTier($userId: ID!) {
    adminUserSpendTier(input: { userId: $userId }) {
      userId
      tier
      isPayer
      totalRevenue
      computedAt
      isForced
      syncedAt
      hasFirstPaidAt
    }
  }
`;

export const GET_MONTH_ID_LIST = gql`
  query getUserMonthIdList($input: GetUserMonthIdListInput!) {
    app {
      getUserMonthIdList(input: $input) {
        list
      }
    }
  }
`;

export const SHOW_MISSION_PROGRESS = gql`
  query getUserMonthlyPass($input: GetUserMonthlyPassInput!) {
    app {
      getUserMonthlyPass(input: $input) {
        userMonthlyPass {
          allMissions {
            id
            dailyMissions {
              id
              mission {
                id
                start
                end
                requiredTier
                tasks {
                  type
                  params {
                    __typename
                  }
                }
                rewardXp
              }
              progress
              progressGoal
              completedDate
              claimedDate
              claimedMultiplier
            }
            monthlyMissions {
              id
              mission {
                id
                start
                end
                requiredTier
                tasks {
                  type
                  params {
                    __typename
                  }
                }
                rewardXp
              }
              progress
              progressGoal
              completedDate
              claimedDate
              claimedMultiplier
            }
            extraMissions {
              id
              mission {
                id
                start
                end
                requiredTier
                tasks {
                  type
                  params {
                    __typename
                  }
                }
                rewardXp
              }
              progress
              progressGoal
              completedDate
              claimedDate
              claimedMultiplier
            }
            teamMissions {
              id
              mission {
                id
                start
                end
                requiredTier
                tasks {
                  type
                  params {
                    __typename
                  }
                }
                rewardXp
              }
              progress
              progressGoal
              completedDate
              claimedDate
              claimedMultiplier
            }
          }
        }
      }
    }
  }
`;

export const RESET_MISSION_PROGRESS = gql`
  mutation resetMissionProgress($input: ResetMissionProgressInput!) {
    resetMissionProgress(input: $input) {
      user {
        currentUserMonthlyPass {
          missions {
            ...MonthlyPassMissionsFragment
          }
        }
      }
    }
  }
  ${MONTHLY_PASS_MISSIONS_FRAGMENT}
`;

export const FORCE_REWARDS_FOR_MP_LEADERBOARD = gql`
  mutation forceGrantMPLeaderboardRewards($input: ForceGrantMPLRewardsInput!) {
    forceGrantMPLeaderboardRewards(input: $input)
  }
`;

export const FORCE_MISSION_PROGRESS = gql`
  mutation forceMissionProgress($input: ForceMissionProgressInput!) {
    forceMissionProgress(input: $input) {
      missionProgress {
        id
        progress
        progressGoal
        completedDate
        claimedDate
      }
    }
  }
`;

export const FORCE_CLAIM_MISSION = gql`
  mutation forceClaimMission($input: ForceClaimMissionInput!) {
    forceClaimMission(input: $input) {
      missionProgress {
        id
        progress
        progressGoal
        completedDate
        claimedDate
        claimedMultiplier
      }
    }
  }
`;

export const RESET_SINGLE_MISSION_PROGRESS = gql`
  mutation resetSingleMissionProgress($input: ResetSingleMissionProgressInput!) {
    resetSingleMissionProgress(input: $input) {
      missionProgress {
        id
        progress
        progressGoal
        completedDate
        claimedDate
        claimedMultiplier
      }
    }
  }
`;

const USER_MONTHLY_PASS_CLAIMED_REWARDS_FRAGMENT = gql`
  fragment UserMonthlyPassClaimedRewardsFragment on UserMonthlyPass {
    id
    level
    progressToNextLevel
    xpBuff
    coinBuff
    claimedRewards {
      level
      bestTierClaimed
      tiersClaimed
    }
  }
`;

export const GET_USER_MONTHLY_PASS_CLAIMED_REWARDS = gql`
  query getUserMonthlyPassClaimedRewards($input: GetUserMonthlyPassInput!) {
    app {
      getUserMonthlyPass(input: $input) {
        userMonthlyPass {
          ...UserMonthlyPassClaimedRewardsFragment
        }
      }
    }
  }
  ${USER_MONTHLY_PASS_CLAIMED_REWARDS_FRAGMENT}
`;

export const RESET_USER_MONTHLY_PASS_CLAIMED_REWARDS = gql`
  mutation resetUserMonthlyPassClaimedRewards($input: ResetUserMonthlyPassClaimedRewardsInput!) {
    resetUserMonthlyPassClaimedRewards(input: $input) {
      userMonthlyPass {
        ...UserMonthlyPassClaimedRewardsFragment
      }
    }
  }
  ${USER_MONTHLY_PASS_CLAIMED_REWARDS_FRAGMENT}
`;

// TEST env only: runs the ended-season auto-claim on demand (same grant the 1st-of-month
// cron performs). resetGuard clears the autoClaimedAt guard first so QA can re-fire.
export const TRIGGER_MONTHLY_PASS_AUTO_CLAIM = gql`
  mutation triggerMonthlyPassAutoClaim($input: TriggerMonthlyPassAutoClaimInput!) {
    triggerMonthlyPassAutoClaim(input: $input) {
      grantedCount
      autoClaimedAt
    }
  }
`;

// TEST env only: forces a user's ended-season pass into a claimable state (sets level, clears
// claimedRewards + autoClaimedAt) so the auto-claim has unclaimed rewards to grant.
export const SEED_MONTHLY_PASS_STATE_FOR_TEST = gql`
  mutation seedMonthlyPassStateForTest($input: SeedMonthlyPassStateInput!) {
    seedMonthlyPassStateForTest(input: $input) {
      level
      maxLevel
    }
  }
`;

// TEST env only: read-only preview of the rewards the ended-season auto-claim would grant.
export const PREVIEW_MONTHLY_PASS_AUTO_CLAIM = gql`
  mutation previewMonthlyPassAutoClaim($input: PreviewMonthlyPassAutoClaimInput!) {
    previewMonthlyPassAutoClaim(input: $input) {
      level
      tier
      type
      amount
      itemId
    }
  }
`;

// Same BE mutation as RESET_MISSION_PROGRESS, but selects achievement missions in
// the response so Apollo refreshes the achievement display after reset.
export const RESET_ACHIEVEMENT_PROGRESS = gql`
  mutation resetAchievementProgress($input: ResetMissionProgressInput!) {
    resetMissionProgress(input: $input) {
      user {
        id
        achievementMissions {
          id
          missions {
            ...MissionProgressFragment
          }
        }
      }
    }
  }
  ${MISSION_PROGRESS_FRAGMENT}
`;

export const GET_SP2_LINK_STATE = gql`
  query getLinkStateAdmin($input: Sp3IdInput!) {
    getLinkStateAdmin(input: $input) {
      state
      sp2id
    }
  }
`;

export const GET_SP2_LINK_STATE_ON_SP2 = gql`
  query getLinkStateBySp3IdFromSp2($input: Sp3IdInput!) {
    getLinkStateBySp3IdFromSp2(input: $input) {
      state
      sp2id
    }
  }
`;

export const ADMIN_SET_USER_SPEND_TIER = gql`
  mutation adminSetUserSpendTier($input: AdminSetUserSpendTierInput!) {
    adminSetUserSpendTier(input: $input) {
      userId
      tier
      isPayer
      totalRevenue
    }
  }
`;
export const LINK_TO_SP2_ID = gql`
  mutation linkToSp2Admin($input: Sp2LinkIdPairInput!) {
    linkToSp2Admin(input: $input) {
      error
      errorMessage
      success
      isPending
    }
  }
`;
export const UNLINK_ID_PAIR = gql`
  mutation unlinkIdPair($input: Sp2LinkIdPairInput!) {
    unlinkIdPair(input: $input) {
      error
      errorMessage
      success
      isPending
    }
  }
`;

export const GET_CURRENT_HURDLE_QUIZZES = gql`
  query currentHurdleQuizzes($input: CurrentHurdleQuizzesInput!) {
    currentHurdleQuizzes(input: $input) {
      id
      hasMore
      totalCount
      list {
        ...HurdleQuizFragment
      }
    }
  }
  ${HURDLE_QUIZ_FRAGMENT}
`;

export const RESET_USER_HURDLE_QUIZ = gql`
  mutation resetHurdleQuiz($input: ResetHurdleQuizInput!) {
    resetHurdleQuiz(input: $input) {
      hurdleQuiz {
        ...HurdleQuizFragment
      }
    }
  }
  ${HURDLE_QUIZ_FRAGMENT}
`;

export const UPDATE_USER_HURDLE_QUIZ = gql`
  mutation updateUserHurdleQuiz($input: UpdateUserHurdleQuizInput!) {
    updateUserHurdleQuiz(input: $input) {
      hurdleQuiz {
        ...HurdleQuizFragment
      }
    }
  }
  ${HURDLE_QUIZ_FRAGMENT}
`;

export const COMPLETE_USER_HURDLE_QUIZ = gql`
  mutation completeUserHurdleQuiz($input: CompleteUserHurdleQuizInput!) {
    completeUserHurdleQuiz(input: $input) {
      hurdleQuiz {
        ...HurdleQuizFragment
      }
    }
  }
  ${HURDLE_QUIZ_FRAGMENT}
`;

export const GET_USER_STREAK_HISTORY = gql`
  query streakHistory($input: StreakHistoryInput!) {
    streakHistory(input: $input) {
      currentStreak
      lostGames {
        hurdleQuizId
        previousStreak
        lostDate
        songTitle
        songArtist
      }
    }
  }
`;

export const GET_ACTIVE_USER_SUBSCRIPTION_UPDATE_LOGS = gql`
  query activeUserSubscriptionUpdateLogs($input: ActiveUserSubscriptionUpdateLogsInput!) {
    activeUserSubscriptionUpdateLogs(input: $input) {
      logs {
        userId
        logId
        store
        type
        sku
        environment
        originalTransactionId
        created
        expires
        purchased
        cancelled
        cancelledReason
        autoRenew
        retrying
        valid
        iosWebOrderLineItemId
      }
    }
  }
`;

export const APPLY_USER_SUBSCRIPTION_UPDATE_LOG = gql`
  mutation applyUserSubscriptionUpdateLog($input: ApplyUserSubscriptionUpdateLogInput!) {
    applyUserSubscriptionUpdateLog(input: $input) {
      user {
        ...UserViewerFragment
        isInfluencer
        influencerId
        testCredential
      }
    }
  }
  ${USER_FRAGMENT}
`;

const QUIZ_LIVE_SHOW_USER_FRAGMENT = gql`
  fragment QuizLiveShowUserFragment on QuizLiveShowUser {
    id
    diamondReward
    ghostQuestionId
    reviveQuestionId
    show {
      id
      startDate
      lastQuestionStartDate
      diamondPrize
      rounds {
        id
        questionDuration
        questions {
          id
        }
      }
      winners {
        id
        user {
          id
        }
      }
    }
  }
`;

export const GET_QUIZ_LIVE_SHOW_USER = gql`
  query quizLiveShowUser($userId: ID!, $playDate: Date!) {
    quizLiveShowUser(userId: $userId, playDate: $playDate) {
      ...QuizLiveShowUserFragment
    }
  }
  ${QUIZ_LIVE_SHOW_USER_FRAGMENT}
`;

export const REFUND_LIVE_SHOW = gql`
  mutation refundLiveShow($userId: ID!, $showId: ID!) {
    refundLiveShowUserBoosts(input: { userId: $userId, showId: $showId }) {
      user {
        id
      }
    }
  }
`;

// Year In Review section
export const YEAR_IN_REVIEW_FRAGMENT = gql`
  fragment YearInReviewFragment on YearInReview {
    id
    year
    seen
    available
  }
`;

export const GET_ALL_YEAR_IN_REVIEW = gql`
  query getAllYearInReview($userId: String!, $type: UserSearchType!) {
    user(property: $userId, type: $type) {
      getAllYearInReview {
        ...YearInReviewFragment
      }
    }
  }
  ${YEAR_IN_REVIEW_FRAGMENT}
`;

export const CHANGE_AVAILABLE_DATE_YIR = gql`
  mutation changeAvailableDateYIR($userId: ID!, $year: Int!, $date: Date!) {
    changeAvailableDateYearInReview(input: { userId: $userId, year: $year, date: $date }) {
      ...YearInReviewFragment
    }
  }
  ${YEAR_IN_REVIEW_FRAGMENT}
`;

export const GET_TEAM_HEAT_INFO = gql`
  query getTeamHeatInfo($property: String!, $type: UserSearchType!) {
    user(property: $property, type: $type) {
      id
      team {
        id
        availableBoosts
        boosts {
          type
          expireTimestamp
        }
        teamHeat {
          id
          currentScore
          lastUpdateScore
          lastUpdateScoreTimestamp
          maxHeatExpireTimestamp
          isMaxHeat
        }
        teamMonthlyPass {
          id
          teamHeatIteration
          availableTeamHeatMilestone
        }
      }
      currentUserMonthlyPass {
        id
        claimedTeamHeatMilestone
      }
    }
  }
`;

export const UPDATE_TEAM_HEAT_INFO = gql`
  mutation updateTeamHeatInfo($input: TeamHeatUpdateInput!) {
    updateTeamHeat(input: $input) {
      teamHeat {
        id
        currentScore
        lastUpdateScore
        lastUpdateScoreTimestamp
        maxHeatExpireTimestamp
        isMaxHeat
      }
      teamMonthlyPass {
        id
        teamHeatIteration
        availableTeamHeatMilestone
      }
    }
  }
`;

export const UPDATE_TEAM_BOOSTS = gql`
  mutation updateTeamBoosts($input: UpdateTeamBoostsInput!) {
    updateTeamBoosts(input: $input) {
      team {
        id
        availableBoosts
        boosts {
          type
          expireTimestamp
        }
      }
    }
  }
`;

export const RESET_CLAIMED_TEAM_HEAT_MILESTONES = gql`
  mutation resetClaimedTeamHeatMilestones($input: ResetTeamHeatClaimedMilestonesInput!) {
    resetClaimedTeamHeatMilestones(input: $input) {
      userMonthlyPass {
        id
        claimedTeamHeatMilestone
      }
    }
  }
`;

export const USER_EMOJI_PACK_FRAGMENT = gql`
  fragment UserEmojiPackFragment on UserEmojiPackPaginated {
    id
    totalCount
    hasMore
    list {
      isActive
      availability {
        isAvailable
        type
      }
      emojiPack {
        id
        name
        asset
        layout
        emojis {
          id
          asset
          name
          utfCode
        }
      }
    }
  }
`;

export const USER_EMOJI_PACKS = gql`
  query userEmojiPacks($property: String!, $type: UserSearchType!, $limit: Int, $offset: Int) {
    user(property: $property, type: $type) {
      id
      emojiPacks(limit: $limit, offset: $offset) {
        ...UserEmojiPackFragment
      }
    }
  }
  ${USER_EMOJI_PACK_FRAGMENT}
`;

export const DELETE_USER_EMOJI_PACK = gql`
  mutation deleteUserEmojiPack($input: DeleteUserEmojiPackInput!) {
    deleteUserEmojiPack(input: $input) {
      user {
        id
        emojiPacks {
          ...UserEmojiPackFragment
        }
      }
    }
  }
  ${USER_EMOJI_PACK_FRAGMENT}
`;

export const UPDATE_USER_EMOJI_PACK = gql`
  mutation updateUserEmojiPack($input: UpdateUserEmojiPackInput!) {
    updateUserEmojiPack(input: $input) {
      user {
        id
        emojiPacks {
          ...UserEmojiPackFragment
        }
      }
    }
  }
  ${USER_EMOJI_PACK_FRAGMENT}
`;

export const ADD_USER_EMOJI_PACK = gql`
  mutation addUserEmojiPack($input: AddUserEmojiPackInput!) {
    addUserEmojiPack(input: $input) {
      user {
        id
        emojiPacks {
          ...UserEmojiPackFragment
        }
      }
    }
  }
  ${USER_EMOJI_PACK_FRAGMENT}
`;

export const RESET_EMOJI_PACK_TUTOR = gql`
  mutation resetEmojiPackTutorial($userId: ID!) {
    resetEmojiPackTutorial(userId: $userId) {
      user {
        id
        emojiPacks {
          ...UserEmojiPackFragment
        }
      }
    }
  }
  ${USER_EMOJI_PACK_FRAGMENT}
`;

export const RESET_USER_DAILY_EMOJIS = gql`
  mutation resetDailyEmojis($userId: ID!) {
    resetDailyEmojis(userId: $userId)
  }
`;

const USER_EXPORTS_FRAGMENT = gql`
  fragment UserExportsFragment on UserExport {
    id
    requested
    processed
    status
    jobStatus
    jobStartedAt
    exportedAt
    fileExpiresAt
  }
`;

export const GET_USER_EXPORTS = gql`
  query getUserExports($input: UserExportsInput!) {
    userExports(input: $input) {
      userExports {
        ...UserExportsFragment
      }
    }
  }
  ${USER_EXPORTS_FRAGMENT}
`;

export const GET_SIGNED_DOWNLOAD_URL = gql`
  query getSignedDownloadUrl($input: GetSignedDownloadUrlInput!) {
    getSignedDownloadUrl(input: $input) {
      url
    }
  }
`;

export const START_EXPORT_JOB = gql`
  mutation startExportJob($input: StartExportJobInput!) {
    startExportJob(input: $input) {
      userExport {
        ...UserExportsFragment
      }
    }
  }
  ${USER_EXPORTS_FRAGMENT}
`;

export const CREATE_EXPORT_REQUEST = gql`
  mutation createExportRequest($input: CreateExportRequestInput!) {
    createExportRequest(input: $input) {
      userExport {
        ...UserExportsFragment
      }
    }
  }
  ${USER_EXPORTS_FRAGMENT}
`;

export const PROCESS_EXPORT_REQUEST = gql`
  mutation processExportRequest($input: ProcessExportRequestInput!) {
    processExportRequest(input: $input) {
      userExport {
        ...UserExportsFragment
      }
    }
  }
  ${USER_EXPORTS_FRAGMENT}
`;

export const DELETE_USER_CALENDAR = gql`
  mutation deleteUserCalendar($calendarId: ID!, $userId: ID!) {
    deleteUserCalendar(calendarId: $calendarId, userId: $userId) {
      id
      calendars {
        id
        name
      }
    }
  }
`;

export const GET_MARATHON_EVENT_MILESTONES = gql`
  query getMarathonEventMilestones($id: ID!) {
    liveOps {
      getMarathonEvent(id: $id) {
        id
        milestones {
          numberOfGuesses
          type
        }
      }
    }
  }
`;

export const GET_CURRENT_USER_MARATHON_EVENT = gql`
  query getCurrentUserMarathonEvent($property: String!, $type: UserSearchType!) {
    user(property: $property, type: $type) {
      id
      userMarathonEvent {
        id
        eventId
        checkPoint
        currentRound {
          id
          state
        }
        maxSongReached
        milestoneClaimed {
          index
          isBasicClaimed
          isPlusClaimed
        }
      }
    }
  }
`;

export const UPDATE_USER_MARATHON_EVENT = gql`
  mutation updateUserMarathonEvent($input: UpdateUserMarathonEventInput!) {
    updateUserMarathonEvent(input: $input) {
      userEvent {
        id
        eventId
        checkPoint
        currentRound {
          id
          state
        }
        maxSongReached
        milestoneClaimed {
          index
          isBasicClaimed
          isPlusClaimed
        }
      }
    }
  }
`;

export const RESET_USER_MARATHON_EVENT = gql`
  mutation resetUserMarathonEvent($input: ResetUserMarathonEventInput!) {
    resetUserMarathonEvent(input: $input) {
      userEvent {
        id
        eventId
        checkPoint
        currentRound {
          id
          state
        }
        maxSongReached
        milestoneClaimed {
          index
          isBasicClaimed
          isPlusClaimed
        }
      }
    }
  }
`;

export const GET_USER_MARATHON_ACHIEVEMENTS = gql`
  query getUserMarathonAchievements($property: String!, $type: UserSearchType!) {
    user(property: $property, type: $type) {
      id
      achievementMissions {
        id
        missions {
          ...MissionProgressFragment
        }
      }
    }
  }
  ${MISSION_PROGRESS_FRAGMENT}
`;

export const UPDATE_USER_MARATHON_ACHIEVEMENTS = gql`
  mutation updateUserMarathonAchievementProgress($input: UpdateUMEAchievement!) {
    updateUserMarathonAchievementProgress(input: $input)
  }
`;

export const GET_USER_REDEEM_CODE_HISTORY = gql`
  query getUserRedeemCodeHistory($userId: String!) {
    user(property: $userId, type: BY_ID) {
      id
      redeemCodeRedemptions {
        redeemCodeId
        code
        redeemedAt
        rewards {
          __typename
          ... on Currency {
            amount
          }
          ... on CurrencyBoost {
            amount
            boostType
          }
          ... on CurrencyUnlimitedBoost {
            amount
            unlimitedBoostType
          }
          ... on CurrencyCategoryFragment {
            amount
            category {
              id
            }
          }
          ... on CurrencyCategoryRandom {
            amount
          }
          ... on ContentPackReward {
            contentPack {
              id
            }
          }
          ... on AppSkin {
            id
          }
          ... on Sticker {
            id
          }
          ... on ProfileFrame {
            id
          }
          ... on Badge {
            id
            name
          }
          ... on Poster {
            id
          }
          ... on Emoji {
            id
          }
          ... on EmojiPack {
            id
          }
        }
      }
    }
  }
`;

export const DELETE_USER_REDEEM_CODE_REDEMPTION = gql`
  mutation deleteRedeemCodeRedemption($userId: ID!, $redeemCodeId: ID!) {
    deleteRedeemCodeRedemption(userId: $userId, redeemCodeId: $redeemCodeId)
  }
`;

export const RESET_USER_PRA_COOLDOWN = gql`
  mutation resetUserProgressiveAdsCooldown($input: ResetUserPraCooldownInput!) {
    resetUserProgressiveAdsCooldown(input: $input) {
      results {
        section
        reset
        previousCooldownEndsAt
        sessionSnapshotSurvives
      }
    }
  }
`;

// Push catalogue (Dev Tools > User View): every server-triggered push, rendered for one account,
// and the send of one of them to that account's devices. Works on every environment.
export const PUSH_NOTIFICATION_CATALOG_FOR_USER = gql`
  query pushNotificationCatalogForUser($input: PushNotificationCatalogForUserInput!) {
    pushNotificationCatalogForUser(input: $input) {
      language
      languages
      environment
      entries {
        key
        group
        source
        title
        body
        deeplink
        deeplinkTarget
        richCategory
        settingsCategory
        note
      }
    }
  }
`;

export const SEND_PUSH_NOTIFICATION_CATALOG_ENTRY = gql`
  mutation sendPushNotificationCatalogEntry($input: SendPushNotificationCatalogEntryInput!) {
    sendPushNotificationCatalogEntry(input: $input) {
      devices
      success
      failure
      errors
      delaySeconds
      language
      deeplink
      deeplinkNote
      server {
        namespace
        deployed
        gcloudProject
        spannerInstance
        database
      }
      deliveries {
        deviceId
        variation
        systemName
        version
        created
        status
        errorCode
      }
    }
  }
`;

// Songpop Maestro: one player's pass. Status and support grant/revoke work on every
// environment (prod included); the cheat state and resets are TEST only on the backend.
export const ADMIN_SONGPOP_MAESTRO_STATUS_FRAGMENT = gql`
  fragment AdminSongpopMaestroStatusFragment on AdminSongpopMaestroStatus {
    userId
    canResetForTesting
    state
    flag
    grantSource
    dropId
    sku
    store
    transactionId
    purchasedAt
    grantedBy
    grantReason
    revokedAt
    revokedBy
    revokeReason
    lastDailyDiamondsClaim
    liveShowFreeUsedThisWeek
    liveShowWeekStart
    founderNumber
    foundersWallHidden
    foundersWallHiddenByAdmin
  }
`;

export const ADMIN_SONGPOP_MAESTRO_STATUS = gql`
  query adminSongpopMaestroStatus($userId: ID!) {
    liveOps {
      adminSongpopMaestroStatus(userId: $userId) {
        ...AdminSongpopMaestroStatusFragment
      }
    }
  }
  ${ADMIN_SONGPOP_MAESTRO_STATUS_FRAGMENT}
`;

export const ADMIN_GRANT_SONGPOP_MAESTRO = gql`
  mutation adminGrantSongpopMaestro($input: AdminGrantSongpopMaestroInput!) {
    adminGrantSongpopMaestro(input: $input) {
      status {
        ...AdminSongpopMaestroStatusFragment
      }
      instantRewardsGranted
    }
  }
  ${ADMIN_SONGPOP_MAESTRO_STATUS_FRAGMENT}
`;

export const ADMIN_EDIT_SONGPOP_MAESTRO = gql`
  mutation adminEditSongpopMaestro($input: AdminEditSongpopMaestroInput!) {
    adminEditSongpopMaestro(input: $input) {
      ...AdminSongpopMaestroStatusFragment
    }
  }
  ${ADMIN_SONGPOP_MAESTRO_STATUS_FRAGMENT}
`;

export const ADMIN_RESET_SONGPOP_MAESTRO_FOR_TESTING = gql`
  mutation adminResetSongpopMaestroForTesting($userId: ID!) {
    adminResetSongpopMaestroForTesting(userId: $userId) {
      status {
        ...AdminSongpopMaestroStatusFragment
      }
      storeConsumed
      storeMessage
    }
  }
  ${ADMIN_SONGPOP_MAESTRO_STATUS_FRAGMENT}
`;

export const ADMIN_REVOKE_SONGPOP_MAESTRO = gql`
  mutation adminRevokeSongpopMaestro($input: AdminSongpopMaestroReasonInput!) {
    adminRevokeSongpopMaestro(input: $input) {
      ...AdminSongpopMaestroStatusFragment
    }
  }
  ${ADMIN_SONGPOP_MAESTRO_STATUS_FRAGMENT}
`;

export const ADMIN_TESTING_TOOLS_AVAILABLE = gql`
  query adminTestingToolsAvailable {
    adminTestingToolsAvailable
  }
`;

export const FILL_USER_PLAYLISTS_XP_ADMIN = gql`
  mutation fillUserPlaylistsXpAdmin($userId: ID!) {
    fillUserPlaylistsXpAdmin(userId: $userId) {
      filled
      alreadyFull
      maxLevel
    }
  }
`;

// Gacha Shop: one player's box states (refresh and recovery applied, as the player sees them) and
// the two QA resets.
export const GACHA_USER_BOX_STATE_FRAGMENT = gql`
  fragment GachaUserBoxStateData on GachaUserBoxState {
    campaignId
    campaignName
    campaignStatus
    boxId
    tier
    openingCount
    claimsInWindow
    claimLimit
    nextRefreshAt
    mysteryPulls
    currentChancePercent
    lastMysteryState
    updatedAt
  }
`;

export const GET_USER_GACHA_BOXES = gql`
  query getUserGachaBoxes($userId: ID!) {
    liveOps {
      userGachaBoxes(userId: $userId) {
        ...GachaUserBoxStateData
      }
    }
  }
  ${GACHA_USER_BOX_STATE_FRAGMENT}
`;

export const RESET_USER_GACHA_MYSTERY_PITY = gql`
  mutation resetUserGachaMysteryPity($userId: ID!, $campaignId: ID!, $boxId: ID!) {
    resetUserGachaMysteryPity(userId: $userId, campaignId: $campaignId, boxId: $boxId) {
      ...GachaUserBoxStateData
    }
  }
  ${GACHA_USER_BOX_STATE_FRAGMENT}
`;

export const RESET_USER_GACHA_CLAIMS = gql`
  mutation resetUserGachaClaims($userId: ID!, $campaignId: ID!, $boxId: ID!) {
    resetUserGachaClaims(userId: $userId, campaignId: $campaignId, boxId: $boxId) {
      ...GachaUserBoxStateData
    }
  }
  ${GACHA_USER_BOX_STATE_FRAGMENT}
`;
